import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWindowWidth } from '@investoro/core';
import dayjs from 'dayjs';
import remarkGfm from 'remark-gfm';
import styled, { keyframes } from 'styled-components';
import { encodeData } from 'utils/encodeAndDecodeFunctions';
import { formatToHour } from 'utils/formatToHour';
import { MessageResponseDto, MessageResponseDtoMessageOwnerEnum } from 'utils/types/ChatbotModels';

import Icon, { IconEnum } from 'components/common/Icon';

import 'dayjs/locale/pl';

import ExpandableTextInput from './ExpandableInput';

const FILTER_GROUPS = [
    {
        module: '',
        keys: [
            'saleFlatsFilter',
            'saleHousesFilter',
            'saleOtherFilter',
            'salePlotsFilter'
        ]
    },
    {
        module: 'rent',
        keys: [
            'rentFlatsFilter',
            'rentHousesFilter',
            'rentOtherFilter',
            'rentPlotsFilter'
        ]
    },
    { module: 'auction', keys: ['auctionFilter'] },
    { module: 'tender', keys: ['tenderFilter'] },
    { module: 'bailiff-notices', keys: ['bailiffNoticeFilter'] },
    { module: 'cooperative', keys: ['cooperativeFilter'] },
    {
        module: 'analysis',
        keys: [
            'analysisFlatsFilter',
            'analysisHousesFilter',
            'analysisOtherFilter',
            'analysisPlotsFilter'
        ]
    }
] as const;

const FILTER_KEYS = FILTER_GROUPS.flatMap((group) => group.keys);

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const LoadingDots = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    height: 12px;

    span {
        display: inline-block;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background-color: var(--color-primary);
        opacity: 0.7;
    }
  
    span:nth-child(1) {
        animation: ${bounce} 0.6s infinite ease-in-out;
    }
  
    span:nth-child(2) {
        animation: ${bounce} 0.6s infinite ease-in-out 0.2s;
    }
  
    span:nth-child(3) {
        animation: ${bounce} 0.6s infinite ease-in-out 0.4s;
    }
`;

const ChatbotContent = styled.div<{ isFullPage?: boolean }>`
    display: flex;
    flex-direction: column;
    ${(props) => props.isFullPage
        ? 'height: calc(100vh - 220px);'
        : 'flex: 1; min-height: 0;'}
`;

const Body = styled.div<{ isFullPage?: boolean }>`
    padding: 0 15px;
    flex: 1;
    min-height: 0;
    ${(props) => props.isFullPage && 'height: calc(100vh - 200px);'}
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
    scroll-behavior: smooth;

    &:hover {
        scrollbar-color: var(--color-alt-second-10) transparent;
    }

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 4px;
        transition: background 0.2s ease;
    }

    &:hover::-webkit-scrollbar-thumb {
        background: var(--color-alt-second-10);
    }
    margin-bottom: ${(props) => props.isFullPage ? '90px' : '10px'};
`;

const DateSection = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 8px 0;
    font-size: var(--font-size-body-4);
    color: var(--color-primary-70);
    font-weight: 500;
`;

const SenderMessage = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-bottom: 10px;
`;

const ReceiverMessage = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    margin-bottom: 10px;
    gap: 2px;
`;

const Message = styled.div`
    padding: 10px 15px;
    border-radius: 12px;
    margin-bottom: 2px;

    p {
        --font-size-body: var(--font-size-body-2);
        --font-weight: 400;
    }
`;

const SenderBubble = styled(Message)`
    background: var(--color-alt);
    border-radius: 20px;
    max-width: 80%;

    p {
        color: #ffffff;
    }
`;

const ReceiverBubble = styled(Message)`
    background: var(--color-alt-second);
    border-radius: 20px;
    max-width: 80%;
    min-width: 64px;

    p {
        color: var(--color-primary);
    }
`;

const BotMessageBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const BotLabel = styled.span`
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: var(--color-alt);
    margin-bottom: 3px;
    padding-left: 2px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 12px 0;
`;

const Button = styled.div`
    color: var(--color-white);
    background-color: var(--color-success);
    cursor: pointer;
    padding: 4px 12px;
    border-radius: var(--border-radius);
    font-size: var(--font-size-body-2);
`;

const Hour = styled.p`
    --font-size-body: 11px;
    --font-weight: 400;
`;

const SuggestedQuestionsContainer = styled.div`
    padding: 8px 15px 4px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SuggestedQuestionsLabel = styled.p`
    color: var(--color-primary);
    opacity: 0.6;
`;

const SuggestedQuestionChip = styled.button`
    background: var(--color-alt-second);
    border: 1px solid var(--color-alt);
    border-radius: 16px;
    padding: 8px 14px;
    font-size: var(--font-size-body-2);
    color: var(--color-primary);
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover {
        background: var(--color-alt);
        color: var(--color-white);
    }
`;

const Bottom = styled.div<{ isDarkMode: boolean; isFullPage?: boolean }>`
    border-radius: 28px;
    background: var(--color-alt-second);
    margin: auto 8px 8px;
    box-shadow: 0px -7px 6px 0px rgba(0, 0, 0, 0.06);

    ${(props) => props.isFullPage && `
        position: sticky;
        bottom: 8px;
        margin: 0 15px 8px;
        z-index: 100;
    `}

    ${(props) => props.isDarkMode && `
        img {
            filter: brightness(0) invert(1);
        }
    `}
`;

const MarkdownContent = styled.div`
    font-size: var(--font-size-body-2);
    font-weight: 400;
    line-height: 1.5;

    p { margin: 0 0 6px; font-size: var(--font-size-body-2); }
    p:last-child { margin-bottom: 0; }
    strong { font-weight: 600; }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const MessageAudioRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    width: 100%;
`;

const AudioLoadingSpinner = styled.div`
    width: 16px;
    height: 16px;
    border: 2px solid var(--color-alt-second);
    border-top-color: var(--color-alt);
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
    flex-shrink: 0;
`;

const MessagePlayBtn = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    background-color: var(--color-alt);
    transition: opacity 0.2s;

    img {
        filter: brightness(0) invert(1);
    }

    &:hover {
        opacity: 0.85;
    }
`;

const HiddenAudio = styled.audio`
    display: none;
`;

const AudioPlaybackButton = styled.button<{ isActive?: boolean }>`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    background-color: ${(props) => props.isActive ? 'var(--color-error)' : 'var(--color-alt)'};
    color: #ffffff;
    transition: background-color 0.2s;

    &:hover {
        opacity: 0.85;
    }
`;

const audioWave = keyframes`
    0%, 100% { height: 3px; }
    50% { height: 14px; }
`;

const AudioWaveContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    height: 18px;
    flex-shrink: 0;
`;

const AudioWaveBar = styled.span<{ delay: string }>`
    width: 3px;
    height: 3px;
    background-color: var(--color-alt);
    border-radius: 2px;
    animation: ${audioWave} 0.8s ease-in-out infinite;
    animation-delay: ${(props) => props.delay};
`;

const ProgressBarContainer = styled.div`
    flex: 1;
    height: 4px;
    background: var(--color-alt-second);
    border-radius: 2px;
    cursor: pointer;
    position: relative;
    min-width: 60px;
`;

const ProgressFill = styled.div<{ progress: number }>`
    height: 100%;
    width: ${(props) => props.progress}%;
    background: var(--color-alt);
    border-radius: 2px;
    position: relative;

    &::after {
        content: '';
        position: absolute;
        right: -5px;
        top: 50%;
        transform: translateY(-50%);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--color-alt);
    }
`;

interface ChatbotConversationProps {
    messages: MessageResponseDto[];
    isLoadingMessage: boolean;
    message: string;
    setMessage: (message: string) => void;
    onSendMessage: () => void;
    onSelectSuggestion?: (question: string) => void;
    isDarkMode: boolean;
    maxRows?: number;
    isFullPage?: boolean;
    setPopupOpen?: (isOpen: boolean) => void;
    suggestedQuestions?: string[] | null;
    onGetMessageAudio?: (messageId: number) => Promise<string | null>;
}

const ChatbotConversation: FC<ChatbotConversationProps> = ({
    messages,
    isLoadingMessage,
    message,
    setMessage,
    onSendMessage,
    onSelectSuggestion,
    isDarkMode,
    maxRows,
    isFullPage,
    setPopupOpen,
    suggestedQuestions,
    onGetMessageAudio
}) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const messagesRef = useRef<HTMLDivElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const audioCacheRef = useRef<Record<number, string>>({});
    const width = useWindowWidth();
    const [activeAudioId, setActiveAudioId] = useState<number | null>(null);
    const [loadingAudioId, setLoadingAudioId] = useState<number | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioProgress, setAudioProgress] = useState(0);

    const requestedAudioRef = useRef<number | null>(null);

    const handlePlayMessage = async (messageId: number) => {
        if (activeAudioId === messageId && audioRef.current) {
            if (audioCacheRef.current[messageId] && audioRef.current.src) {
                if (audioRef.current.paused) {
                    audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
                } else {
                    audioRef.current.pause();
                    setIsPlaying(false);
                }
            }

            return;
        }

        if (loadingAudioId !== null && loadingAudioId !== messageId) return;

        if (audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
            setIsPlaying(false);
        }

        setActiveAudioId(messageId);
        setAudioProgress(0);
        requestedAudioRef.current = messageId;

        const cachedUrl = audioCacheRef.current[messageId];

        if (cachedUrl) {
            if (audioRef.current) {
                audioRef.current.src = cachedUrl;
                audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
            }

            return;
        }

        setIsPlaying(false);
        setLoadingAudioId(messageId);
        const fetchedUrl = await onGetMessageAudio?.(messageId);
        setLoadingAudioId(null);

        if (!fetchedUrl || requestedAudioRef.current !== messageId) return;
        audioCacheRef.current[messageId] = fetchedUrl;

        if (audioRef.current && requestedAudioRef.current === messageId) {
            audioRef.current.src = fetchedUrl;
            audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
        }
    };

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!audioRef.current) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        audioRef.current.currentTime = ratio * audioRef.current.duration;
        setAudioProgress(ratio * 100);
    };

    const scrollToBottom = () => {
        if (messagesRef.current) {
            messagesRef.current.scrollTo(0, messagesRef.current.scrollHeight);
        }
    };

    const firstMessageId = messages[0]?.id;

    useEffect(() => {
        if (audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
            audioRef.current.src = '';
        }

        Object.values(audioCacheRef.current).forEach((url) => URL.revokeObjectURL(url));
        audioCacheRef.current = {};

        requestedAudioRef.current = null;
        setActiveAudioId(null);
        setLoadingAudioId(null);
        setIsPlaying(false);
        setAudioProgress(0);
    }, [firstMessageId]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.removeAttribute('src');
                audioRef.current.load();
            }
            requestedAudioRef.current = null;
            Object.values(audioCacheRef.current).forEach((url) => URL.revokeObjectURL(url));
            audioCacheRef.current = {};
        };
    }, []);

    useEffect(() => {
        if (!pathname) return;

        if (audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause();
        }
        requestedAudioRef.current = null;
        setActiveAudioId(null);
        setLoadingAudioId(null);
        setIsPlaying(false);
        setAudioProgress(0);
    }, [pathname]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoadingMessage]);

    const hasAnyFilter = (msg: MessageResponseDto): boolean => {
        return FILTER_KEYS.some((key) => Boolean(msg[key]));
    };

    const getFilterObject = (msg: MessageResponseDto) => {
        for (const group of FILTER_GROUPS) {
            for (const key of group.keys) {
                const filter = msg[key];
                if (filter) return { filter, module: group.module };
            }
        }

        return null;
    };

    const handleShowResults = (msg: MessageResponseDto) => {
        const result = getFilterObject(msg);
        if (!result) return;
        const module = result.module === '' ? '' : `/${result.module}`;
        const filterData = {
            ...result.filter,
            locations: result.filter.locations || [],
            _timestamp: Date.now()
        };
        const encodedObject = encodeData(filterData);
        navigate(`${module}/ai-filter/${encodedObject}`);

        if (width < 768 && setPopupOpen) {
            setPopupOpen(false);
        }
    };

    const groupMessagesByDate = useCallback(() => {
        const groupedMessages: Record<string, typeof messages> = {};

        messages.forEach((message) => {
            const date = message.createdAt.split('T')[0];

            if (!groupedMessages[date]) {
                groupedMessages[date] = [];
            }
            groupedMessages[date].push(message);
        });

        return groupedMessages;
    }, [messages]);

    const groupedMessages = groupMessagesByDate();
    const allDates = Object.keys(groupedMessages);

    return (
        <ChatbotContent isFullPage={isFullPage}>
            <Body ref={messagesRef} isFullPage={isFullPage}>
                {allDates
                    .map((date) => <React.Fragment key={date}>
                        <DateSection>
                            {dayjs(date).locale('pl').format('D MMM')
                                .toUpperCase()}
                        </DateSection>

                        {groupedMessages[date].map((msg, index) => {
                            const isUserMessage = msg.messageOwner === MessageResponseDtoMessageOwnerEnum.USER_MESSAGE;
                            if (!isUserMessage && msg.message === '') return null;

                            return isUserMessage
                                ? <SenderMessage key={`${msg.createdAt}-${index}`}>
                                    <SenderBubble>
                                        <p>{msg.message}</p>
                                    </SenderBubble>
                                    <Hour>{formatToHour(msg.createdAt)}</Hour>
                                </SenderMessage>
                                : <ReceiverMessage key={`${msg.createdAt}-${index}`}>
                                    <BotMessageBody>
                                        <BotLabel>Investoro AI</BotLabel>
                                        <ReceiverBubble>
                                            <MarkdownContent>
                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.message}</ReactMarkdown>
                                            </MarkdownContent>
                                            {hasAnyFilter(msg) &&
                                                <ButtonContainer>
                                                    <Button
                                                        onClick={() => handleShowResults(msg)}
                                                    >
                                                        Pokaż wyniki
                                                    </Button>
                                                </ButtonContainer>
                                            }
                                            {msg.id && onGetMessageAudio &&
                                                <MessageAudioRow>
                                                    {loadingAudioId === msg.id
                                                        ? <>
                                                            <AudioLoadingSpinner />
                                                            <ProgressBarContainer>
                                                                <ProgressFill progress={0} />
                                                            </ProgressBarContainer>
                                                        </>
                                                        : activeAudioId === msg.id
                                                            ? <>
                                                                <AudioPlaybackButton isActive={isPlaying} onClick={() => handlePlayMessage(msg.id!)}>
                                                                    <Icon icon={isPlaying ? IconEnum.PAUSE_WHITE : IconEnum.PLAY_WHITE} />
                                                                </AudioPlaybackButton>
                                                                {isPlaying &&
                                                                    <AudioWaveContainer>
                                                                        <AudioWaveBar delay="0s" />
                                                                        <AudioWaveBar delay="0.1s" />
                                                                        <AudioWaveBar delay="0.2s" />
                                                                        <AudioWaveBar delay="0.3s" />
                                                                        <AudioWaveBar delay="0.15s" />
                                                                        <AudioWaveBar delay="0.05s" />
                                                                    </AudioWaveContainer>
                                                                }
                                                                <ProgressBarContainer onClick={handleProgressClick}>
                                                                    <ProgressFill progress={audioProgress} />
                                                                </ProgressBarContainer>
                                                            </>
                                                            : <MessagePlayBtn onClick={() => handlePlayMessage(msg.id!)}>
                                                                <Icon icon={IconEnum.AUDIO} />
                                                            </MessagePlayBtn>
                                                    }
                                                </MessageAudioRow>
                                            }
                                        </ReceiverBubble>
                                        <Hour>{formatToHour(msg.createdAt)}</Hour>
                                    </BotMessageBody>
                                </ReceiverMessage>;
                        })}
                    </React.Fragment>)}

                {isLoadingMessage && messages[messages.length - 1]?.messageOwner === MessageResponseDtoMessageOwnerEnum.BOT_MESSAGE && messages[messages.length - 1]?.message === '' &&
                <ReceiverMessage>
                    <BotMessageBody>
                        <ReceiverBubble>
                            <LoadingDots>
                                <span></span>
                                <span></span>
                                <span></span>
                            </LoadingDots>
                        </ReceiverBubble>
                    </BotMessageBody>
                </ReceiverMessage>
                }
            </Body>

            {suggestedQuestions && suggestedQuestions.length > 0 &&
                <SuggestedQuestionsContainer>
                    <SuggestedQuestionsLabel>Wybierz pytanie, lub napisz własne</SuggestedQuestionsLabel>
                    {suggestedQuestions.map((question, index) => <SuggestedQuestionChip
                        key={index}
                        onClick={() => onSelectSuggestion?.(question)}
                        disabled={isLoadingMessage}
                    >
                        {question}
                    </SuggestedQuestionChip>)}
                </SuggestedQuestionsContainer>
            }

            <Bottom isDarkMode={isDarkMode} isFullPage={isFullPage}>
                {onGetMessageAudio &&
                    <HiddenAudio
                        ref={audioRef}
                        onTimeUpdate={() => {
                            const el = audioRef.current;

                            if (el && el.duration) {
                                setAudioProgress(el.currentTime / el.duration * 100);
                            }
                        }}
                        onEnded={() => {
                            setIsPlaying(false);
                            setAudioProgress(0);
                        }}
                    />
                }
                <ExpandableTextInput
                    value={message}
                    onChange={setMessage}
                    onSend={onSendMessage}
                    placeholder="Napisz wiadomość..."
                    disabled={isLoadingMessage}
                    isDarkMode={isDarkMode}
                    maxRows={maxRows || 4}
                    isFullPage={isFullPage}
                />
            </Bottom>
        </ChatbotContent>
    );
};

export default ChatbotConversation;
